import moment from 'moment';
import { DynamicObjectInterface } from '../../sdk/type';
import { getRoundedNumber } from './common';
import { decryptString } from './cryptoUtils';
import { resolveGenderAgeThreshold } from './subChallenge';

interface SubKpi {
  indicatorName: string;
  indicatorMeasurementUnit: string;
  subKpiCode: string;
}

const generateDynamicExcelHeaders = (subKpiCode: SubKpi[], checkInDays: number[] = []): string[] => {
  const preIndicatorHeaders = [
    'Team Name',
    'Team ID',
    'Team Description',
    'Team Start Date DD-MM-YYYY',
    'Team End Date DD-MM-YYYY',
    'Team Status',
    'Team Size',
    'Participation Type',
    'Sub-Challenge',
    'Name',
    'ABO ID',
    'Party ID',
    'is Team Leader',
    'is New ABO',
    'Contact',
    'Email',
    'Address',
    'Gender',
    'Age Group',
    'Account Type',
    'Current Pin Level',
    'Highest Pin Achieved',
    'BA/LOA Name',
    'Remark',
    'Status',
    'Order Number',
    'Product Name',
    'Order Qualified',
  ];

  const dynamicIndicators = subKpiCode.map(
    (kpi: DynamicObjectInterface) => `${kpi.indicatorName} (${kpi.indicatorMeasurementUnit})`,
  );

  const postDiffDynamicIndicators = subKpiCode.map(
    (kpi: DynamicObjectInterface) => `${kpi.indicatorName} diff (${kpi.indicatorMeasurementUnit})`,
  );

  const preAssessmentsHeaders = ['Date', ...dynamicIndicators, 'Photo - Front', 'Photo - Back'];

  const postAssessmentsHeaders = ['Date', ...dynamicIndicators, 'Photo - Front', 'Photo - Back'];

  const postIndicatorHeaders = [
    ...postDiffDynamicIndicators,
    'Individual Completed',
    'Achieved Individual Criteria',
    'Achieved Team Criteria',
    'Task Completed',
  ];

  const checkInHeaders = checkInDays.map(day => `Check-In Day ${day}`);

  return [
    ...preIndicatorHeaders,
    'Test Device',
    ...preAssessmentsHeaders,
    ...postAssessmentsHeaders,
    ...postIndicatorHeaders,
    ...checkInHeaders,
  ];
};

const generateDynamicExcelColumnKeys = (subKpiCode: SubKpi[], checkInDays: number[] = []): { key: string }[] => {
  const preIndicatorKeys = [
    { key: 'teamName' },
    { key: 'teamId' },
    { key: 'teamDescription' },
    { key: 'teamStartDate' },
    { key: 'teamEndDate' },
    { key: 'teamStatus' },
    { key: 'teamSize' },
    { key: 'participationType' },
    { key: 'subChallengeName' },
    { key: 'name' },
    { key: 'aboId' },
    { key: 'partyId' },
    { key: 'isTeamLeader' },
    { key: 'isNewAbo' },
    { key: 'phone' },
    { key: 'email' },
    { key: 'address' },
    { key: 'gender' },
    { key: 'ageGroup' },
    { key: 'accountType' },
    { key: 'currentPinLevel' },
    { key: 'highestPinAchieved' },
    { key: 'baLoaName' },
    { key: 'remark' },
    { key: 'enrollmentStatus' },
    { key: 'orderNumber' },
    { key: 'productName' },
    { key: 'orderQualified' },
  ];

  const preAssessmentDynamicIndicators = subKpiCode.map((kpi: DynamicObjectInterface) => {
    return { key: generateSensitiveKeyFromIndicator(kpi.subKpiCode, 'weightIn') };
  });

  const postAssessmentDynamicIndicators = subKpiCode.map((kpi: DynamicObjectInterface) => {
    return { key: generateSensitiveKeyFromIndicator(kpi.subKpiCode, 'weightOut') };
  });

  const postDiffDynamicIndicators = subKpiCode.map((kpi: DynamicObjectInterface) => {
    return { key: generateSensitiveKeyFromIndicator(kpi.subKpiCode, 'participant', 'Diff') };
  });

  const preAssessmentsHeaders = [
    { key: 'weightInDate' },
    ...preAssessmentDynamicIndicators,
    { key: 'weightInPhotoFront' },
    { key: 'weightInPhotoBack' },
  ];

  const postAssessmentsHeaders = [
    { key: 'weightOutDate' },
    ...postAssessmentDynamicIndicators,
    { key: 'weightOutPhotoFront' },
    { key: 'weightOutPhotoBack' },
  ];

  const postIndicatorHeaders = [
    ...postDiffDynamicIndicators,
    { key: 'individualCompleted' },
    { key: 'achievedIndividualCriteria' },
    { key: 'achievedGroupCriteria' },
    { key: 'taskCompleted' },
  ];

  const checkInKeys = checkInDays.map(day => ({ key: `checkInDay${day}` }));

  return [
    ...preIndicatorKeys,
    { key: 'testDevice' },
    ...preAssessmentsHeaders,
    ...postAssessmentsHeaders,
    ...postIndicatorHeaders,
    ...checkInKeys,
  ];
};

const generateCamelCaseKeyFromIndicator = (indicatorName: string, prefix: string = '', suffix: string = ''): string => {
  const camel = indicatorName
    .replace(/[^a-zA-Z0-9 ]/g, '') // remove non-alphanumeric except spaces
    .trim()
    .split(/\s+/) // split by one or more spaces
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join('');

  const base = camel.charAt(0).toUpperCase() + camel.slice(1); // Capitalize first for combining

  return `${prefix}${base}${suffix}`;
};

const generateSensitiveKeyFromIndicator = (indicatorName: string, prefix: string = '', suffix: string = ''): string => {
  const cleaned = indicatorName.replace(/[^a-zA-Z0-9 \-_.()]/g, '').trim(); // allow letters, numbers, space, dash, underscore
  return `${prefix}${cleaned}${suffix}`;
};

// Resolve an assessment indicator's config (percentageCalculation / disablePreAssessment
// / targetFrom flags) by subKpiCode for a given participant row. MULTI campaigns scope
// indicators to the participant's enrolled sub-challenge; otherwise the base
// participant.rules[4] list applies. Mirrors the resolution used in the admin portal.
const resolveIndicatorConfig = (item: DynamicObjectInterface, subKpiCode: string): DynamicObjectInterface => {
  const configJson = item.campaign?.configJson;
  let indicators: DynamicObjectInterface[] = [];
  if (configJson?.campaignMode === 'MULTI' && item.subChallengeId) {
    const sc = (configJson?.activities?.subChallenges ?? []).find(
      (s: DynamicObjectInterface) => s.subChallengeId === item.subChallengeId,
    );
    indicators = sc?.rules?.[4]?.subKpiCode ?? [];
  } else {
    indicators = configJson?.activities?.participant?.rules?.[4]?.subKpiCode ?? [];
  }
  return indicators.find((i: DynamicObjectInterface) => i.subKpiCode === subKpiCode) ?? {};
};

const getValueForKey = (key: string, item: DynamicObjectInterface): DynamicObjectInterface => {
  // Handle known static keys directly
  switch (key) {
    case 'teamName':
      return item.teamName;
    case 'teamId':
      return item.teamId;
    case 'teamDescription':
      return item.team?.teamDescription;
    case 'teamStartDate':
      return item.team?.teamStartDate ? moment(item.team?.teamStartDate).format('DD-MM-YYYY') : '';
    case 'teamEndDate':
      return item.team?.teamEndDate ? moment(item.team?.teamEndDate).format('DD-MM-YYYY') : '';
    case 'teamStatus':
      return item.team?.teamStatus;
    case 'teamSize':
      return item.team?.participationType === 'ind' ? 'N/A' : item.team?.teamSize;
    case 'participationType':
      return item.team?.participationType === 'ind' ? 'Individual' : 'Team';
    case 'name':
      return item.name;
    case 'aboId':
      return item.loginId;
    case 'partyId':
      return item.partyId;
    case 'isTeamLeader':
      return item.isTeamLeader ? 'Y' : 'N';
    case 'isNewAbo':
      return item.isNewAbo === 1 ? 'Y' : 'N';
    case 'phone':
      return item.phone;
    case 'email':
      return item.email;
    case 'address':
      return item.address;
    case 'gender':
      return item.gender;
    case 'ageGroup':
      return item.ageGroup ?? '';
    case 'subChallengeName': {
      const subChallenges = item.campaign?.configJson?.activities?.subChallenges ?? [];
      return (
        subChallenges.find((sc: DynamicObjectInterface) => sc.subChallengeId === item.subChallengeId)?.name ?? ''
      );
    }
    case 'testDevice': {
      // Device chosen at assessment is stored on weightIn.machine (device code);
      // resolve it to its configured name from the participant's sub-challenge.
      const deviceCode = item.weightIn?.machine ?? item.weightOut?.machine;
      if (!deviceCode) return '';
      const subChallenges = item.campaign?.configJson?.activities?.subChallenges ?? [];
      const sc = subChallenges.find((s: DynamicObjectInterface) => s.subChallengeId === item.subChallengeId);
      return sc?.testDevices?.find((d: DynamicObjectInterface) => d.code === deviceCode)?.name ?? deviceCode;
    }
    case 'accountType':
      return item.accounType;
    case 'currentPinLevel':
      return item.pinLevel;
    case 'highestPinAchieved':
      return item.highestPinAchieved;
    case 'baLoaName':
      return item.baLoaName;
    case 'remark':
      return item.remark;
    case 'enrollmentStatus':
      return item.enrollmentStatus;
    case 'orderNumber':
      return item?.purchase?.[0]?.orderNumber;
    case 'productName':
      return item?.purchase?.[0]?.orderName;
    case 'orderQualified':
      return item?.purchase?.[0]?.orderStatus === 1 ? 'Y' : 'N';
    case 'weightInDate':
      return item.weightIn?.weightedDate ? moment(item.weightIn.weightedDate).format('DD-MM-YYYY') : '';
    case 'weightOutDate':
      return item.weightOut?.weightedDate ? moment(item.weightOut.weightedDate).format('DD-MM-YYYY') : '';
    case 'weightInPhotoFront':
      return item.weightIn?.weightInPhotoFront ? 'Y' : 'N';
    case 'weightInPhotoBack':
      return item.weightIn?.weightInPhotoBack ? 'Y' : 'N';
    case 'weightOutPhotoFront':
      return item.weightOut?.weightOutPhotoFront ? 'Y' : 'N';
    case 'weightOutPhotoBack':
      return item.weightOut?.weightOutPhotoBack ? 'Y' : 'N';
    case 'achievedIndividualCriteria':
      return item.isIndividualAchieved === 1 ? 'Y' : 'N';
    case 'achievedGroupCriteria':
      return item.isGroupAchieved === 1 ? 'Y' : 'N';
    // Individual Completed: mirrors the admin Progress Details "Completed" field
    // (both pre- and post-assessment submitted). '-' when not yet evaluated.
    case 'individualCompleted':
      return item.isIndividualCompleted === null || item.isIndividualCompleted === undefined
        ? '-'
        : item.isIndividualCompleted === 1
          ? 'Y'
          : 'N';
    // Task Completed: mirrors the admin "Task Completed" field — all configured
    // check-in days for the participant's sub-challenge have a submission. Blank when
    // the sub-challenge has no check-in configured.
    case 'taskCompleted': {
      const configJson = item.campaign?.configJson;
      const sc = (configJson?.activities?.subChallenges ?? []).find(
        (s: DynamicObjectInterface) => s.subChallengeId === item.subChallengeId,
      );
      const days = Array.from(
        new Set(
          ((sc?.checkIn?.days ?? []) as (number | string)[])
            .map(d => Number(d))
            .filter(d => !isNaN(d) && d > 0),
        ),
      );
      if (days.length === 0) return '';
      const submittedDays = new Set(
        (Array.isArray(item.checkIns) ? item.checkIns : [])
          .map((c: DynamicObjectInterface) => Number(c?.day))
          .filter((d: number) => !isNaN(d)),
      );
      return days.every(d => submittedDays.has(d)) ? 'Y' : 'N';
    }
  }

  if (key.startsWith('weightIn')) {
    const subKey = key.replace('weightIn', '');
    return item.weightIn?.otherIndicators?.[subKey]
      ? getRoundedNumber(decryptString(item.weightIn.otherIndicators[subKey]))
      : '';
  }

  if (key.startsWith('weightOut')) {
    const subKey = key.replace('weightOut', '');
    return item.weightOut?.otherIndicators?.[subKey]
      ? getRoundedNumber(decryptString(item.weightOut.otherIndicators[subKey]))
      : '';
  }

  if (key.startsWith('participant') && key.endsWith('Diff')) {
    const subKey = key.replace('participant', '').replace('Diff', '');
    const weightInValue = item.weightIn?.otherIndicators?.[subKey]
      ? getRoundedNumber(decryptString(item.weightIn.otherIndicators[subKey]))
      : 0;
    const weightOutValue = item.weightOut?.otherIndicators?.[subKey]
      ? getRoundedNumber(decryptString(item.weightOut.otherIndicators[subKey]))
      : 0;

    const indicator = resolveIndicatorConfig(item, subKey);
    const hasPost =
      item.weightOut?.otherIndicators?.[subKey] !== undefined &&
      item.weightOut?.otherIndicators?.[subKey] !== null &&
      !!item.weightOut?.weightedDate;

    // Win/lose indicators (mirrors the participant + admin views): gender/age-threshold
    // (e.g. Grip Strength) and disable/ignore-pre (e.g. Sit to Stand) indicators have no
    // meaningful numeric diff — judge win/lose against the resolved threshold / target
    // and export "/" (win) or "x" (lose) instead of a number. Blank until a post value.
    const genderAgeThreshold = resolveGenderAgeThreshold(indicator, item.gender, item.ageGroup);
    const isThresholdIndicator = !!indicator?.indicatorWinningCriteria && genderAgeThreshold !== null;
    const isDisablePreIndicator =
      !isThresholdIndicator && (indicator?.disablePreAssessment === 1 || indicator?.ignorePreAssessment === 1);

    if (isThresholdIndicator || isDisablePreIndicator) {
      if (!hasPost) return '';
      const standard = isThresholdIndicator ? (genderAgeThreshold as number) : parseFloat(indicator?.targetFrom);
      if (isNaN(standard)) return '';
      const achieved =
        indicator?.indicatorWinningCriteria === 'loss' ? weightOutValue <= standard : weightOutValue >= standard;
      return achieved ? '/' : 'x';
    }

    // Standard indicators: apply the same per-indicator diff logic as the admin portal
    // ([partiId].tsx): a percentage indicator is (post - pre) / pre * 100 (i.e.
    // improvement over the ORIGINAL pre value), otherwise a plain (post - pre) difference.
    let weightOutDiffValues = 0;
    if (weightInValue !== null && weightOutValue !== null) {
      if (indicator?.percentageCalculation === 1) {
        weightOutDiffValues =
          weightInValue !== 0 ? getRoundedNumber(((weightOutValue - weightInValue) / weightInValue) * 100) : 0;
      } else {
        weightOutDiffValues = getRoundedNumber(weightOutValue - weightInValue);
      }
    }

    // Percentage indicators show a trailing "%" so the report matches the portal display.
    return indicator?.percentageCalculation === 1
      ? `${weightOutDiffValues.toFixed(1)}%`
      : weightOutDiffValues.toFixed(1);
  }

  // Check-in day columns: show the submission date for that day, else 'N'.
  if (key.startsWith('checkInDay')) {
    const day = Number(key.replace('checkInDay', ''));
    const submission = (Array.isArray(item.checkIns) ? item.checkIns : []).find(
      (c: DynamicObjectInterface) => Number(c?.day) === day,
    );
    if (!submission) return 'N';
    return submission.checkInDate ? moment(submission.checkInDate).format('DD-MM-YYYY') : 'Y';
  }

  return '';
};

// Convert column index (e.g. 1, 27) to Excel letter (A, AA, etc.)
const getExcelAlpha = (col: number): string => {
  let alpha = '';
  while (col > 0) {
    const rem = (col - 1) % 26;
    alpha = String.fromCharCode(65 + rem) + alpha;
    col = Math.floor((col - 1) / 26);
  }
  return alpha;
};

export {
  generateDynamicExcelHeaders,
  generateDynamicExcelColumnKeys,
  generateCamelCaseKeyFromIndicator,
  generateSensitiveKeyFromIndicator,
  getValueForKey,
  getExcelAlpha,
};
